/**
 * Centralized Axios Instance with Auth Interceptor
 * 
 * This module creates a configured axios instance that automatically
 * adds authentication headers to all API requests.
 * 
 * Usage:
 *   import api from '@/config/axiosInstance';
 *   const response = await api.get('/projects');
 *   const response = await api.post('/auth/login', data);
 * 
 * Benefits:
 * - No need to manually pass getAuthHeaders() to every component
 * - Consistent auth handling across the entire app
 * - Automatic token refresh and error handling can be added here
 */
import axios from 'axios';
import { getApiUrl } from './api';

// Create axios instance with base URL
const api = axios.create({
  baseURL: getApiUrl(),
  timeout: 60000, // 60 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - adds auth token to every request
api.interceptors.request.use(
  (config) => {
    // Get token from localStorage - key is 'auth_token' as set by AuthContext
    const token = localStorage.getItem('auth_token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle common errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Handle 401 Unauthorized - could redirect to login
    if (error.response?.status === 401) {
      // Don't redirect for login/signup endpoints
      const url = error.config?.url || '';
      if (!url.includes('/auth/login') && !url.includes('/auth/signup')) {
        console.warn('[API] Unauthorized request:', url);
        // Could emit an event or redirect to login here
      }
    }
    
    // Handle 502/503/504 gateway errors
    if ([502, 503, 504].includes(error.response?.status)) {
      console.error('[API] Gateway error:', error.response?.status, error.config?.url);
    }
    
    return Promise.reject(error);
  }
);

export default api;

// Also export for named imports
export { api };
